import { formatAmount } from './format-amount';
import { invoiceTypeLabel } from './invoice-label';

import type { InvoiceRow, PaymentRow } from '../types';

export function capitalize(value: string): string {
  return value.length === 0 ? value : `${value[0]?.toUpperCase() ?? ''}${value.slice(1)}`;
}

function titleCase(value: string | null): string | null {
  return value === null ? null : capitalize(value);
}

function money(amount: string | null, currency: string | null): string | null {
  return amount === null ? null : formatAmount(amount, currency);
}

export interface InvoiceInfoField {
  // A function label resolves per invoice — needed where the verb depends on
  // the direction (purchase invoices are raised, sale invoices are issued).
  readonly label: string | ((invoice: InvoiceRow) => string);
  readonly value: (invoice: InvoiceRow) => string | null;
}

export function infoFieldLabel(field: InvoiceInfoField, invoice: InvoiceRow): string {
  return typeof field.label === 'function' ? field.label(invoice) : field.label;
}

function isPurchase(invoice: InvoiceRow): boolean {
  return (invoice.invoice_direction ?? '').toLowerCase() === 'purchase';
}

export interface InvoiceInfoGroup {
  readonly key: string;
  readonly fields: readonly InvoiceInfoField[];
}

// The INVOICE INFORMATION block renders each group as its own grid, divided
// by a rule — matching Launchpad's identity → amounts → dates layout. Adding
// a field is a data change, not new JSX. Rows whose value resolves to null are
// skipped; "Formally Issued" always resolves to a non-null string. Status is
// not a group field — it renders as a chip in the section header.
export const INVOICE_INFO_GROUPS: readonly InvoiceInfoGroup[] = [
  {
    key: 'identity',
    fields: [
      { label: 'Invoice Number', value: (inv) => inv.invoice_number },
      { label: 'Type', value: (inv) => invoiceTypeLabel(inv) },
      { label: 'Direction', value: (inv) => titleCase(inv.invoice_direction) },
      { label: 'Order ID', value: (inv) => inv.order_id },
      { label: 'BL Order', value: (inv) => inv.bl_order_name },
      { label: 'Company', value: (inv) => inv.company_name },
      { label: 'Issuer', value: (inv) => inv.issuer_company_name },
    ],
  },
  {
    // The legal stamp written at issue. All fields read "—" on drafts and on
    // historic rows issued before entity stamping existed; the name/summary
    // fields are hydrated by the detail endpoint.
    key: 'legal-identity',
    fields: [
      { label: 'Legal Number', value: (inv) => inv.legal_number },
      { label: 'Issuing Entity', value: (inv) => inv.issuer_entity_name },
      { label: 'Issuing Bank', value: (inv) => inv.issuer_bank_summary },
      { label: 'Issued By', value: (inv) => inv.issued_by_name },
    ],
  },
  {
    key: 'amounts',
    fields: [
      { label: 'Currency', value: (inv) => inv.currency },
      { label: 'Amount %', value: (inv) => inv.amount__ },
      { label: 'Amount (MT)', value: (inv) => inv.amount_qt_mt },
      { label: 'Total Amount', value: (inv) => money(inv.total_amount, inv.currency) },
      { label: 'VAT', value: (inv) => money(inv.vat_amount, inv.currency) },
    ],
  },
  {
    // Launchpad shows no invoice-level Paid Date here — paid info lives in
    // the PAYMENTS section below (per-payment Paid Date + paid total).
    key: 'dates',
    fields: [
      { label: 'Issue Date', value: (inv) => inv.issue_date },
      // "Formally Issued" reflects whether the invoice was issued in the system
      // (issued_at), NOT the printed issue_date — legacy/imported invoices carry
      // an issue_date but were never formally issued, so they read "Draft (not
      // issued)" here just like in Launchpad.
      {
        label: (inv) => (isPurchase(inv) ? 'Formally Raised' : 'Formally Issued'),
        value: (inv) => {
          if (inv.issued_at !== null) return inv.issued_at.slice(0, 10);
          return isPurchase(inv) ? 'Draft (not raised)' : 'Draft (not issued)';
        },
      },
      { label: 'Original Due Date', value: (inv) => inv.original_due_date },
      { label: 'Expected Due Date', value: (inv) => inv.actual_due_date },
    ],
  },
];

export interface PaymentField {
  readonly label: string;
  readonly value: (payment: PaymentRow) => string | null;
}

// Per-payment columns, rendered as wrapping label-over-value cells.
export const PAYMENT_FIELDS: readonly PaymentField[] = [
  { label: 'Type', value: (p) => p.payment_type },
  { label: 'Direction', value: (p) => titleCase(p.payment_direction) },
  { label: 'Amount', value: (p) => formatAmount(p.total_amount_paid, p.currency) },
  { label: 'Paid Date', value: (p) => p.paid_date ?? 'No paid date' },
  { label: 'Reference', value: (p) => p.reference_note },
  {
    label: 'AW Transfer ID',
    value: (p) => p.transfer_ref ?? p.airwallex_transfer_id ?? p.bank_reference,
  },
];
